import React, { useEffect, useId, useState } from 'react';
import { Send, Loader2, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { commsApi } from './phase567Api';
import { blockText } from './sendStatus';
import { UNCONFIRMED_HINT } from './MessageItem';
import { toast } from '../hooks/use-toast';
import { isSpendApprovalError } from './api';
import { quietHoursDetail, confirmQuietHours } from './quietHours';
import { useAuth } from './AuthContext';
import { TemplatePicker, canManageTemplates } from './MessageTemplates';
import RichEmailEditor, { escapeHtml, signatureHtml } from './RichEmailEditor';
import SignatureEditor from './SignatureEditor';

const LABELS = { sms: 'SMS', email: 'Email' };

// Whitespace is ignored when asking "is this just the signature?": the editor's
// text puts blank lines between paragraphs that the stored signature lacks.
const squash = (t) => String(t || '').replace(/\s+/g, '');

// Channels this conversation can send on. A GHL thread sends only on its own
// channel and only through GHL (ticket 135 S2), so call threads get none; any
// channel whose transport is off is hidden (S0).
export function sendableChannels(conv, transports) {
  if (conv?.source === 'ghl') {
    return transports?.[conv.channel] === 'ghl' ? [conv.channel] : [];
  }
  return ['sms', 'email'].filter((ch) => transports?.[ch] !== 'off');
}

// Ticket 142: the Inbox composer works at the contact level (a contact can
// have several per-channel conversations, some not created yet), so it
// can't derive its options from a single `conv`. Same GHL-only-via-GHL rule,
// applied across every text channel instead of just the one thread's.
export function sendableChannelsForContact(isGhl, transports) {
  if (isGhl) return ['sms', 'email'].filter((ch) => transports?.[ch] === 'ghl');
  return ['sms', 'email'].filter((ch) => transports?.[ch] !== 'off');
}

// Shared composer for the Inbox thread and the contact Communications tab.
// Remount it per conversation (key={conversation_id}) so draft state resets.
// `status` is the contact's send-status (ticket 141): a blocked channel
// disables Send with the reason. A 409/503 from the server calls onBlocked
// so the parent re-reads the status.
//
// Ticket 142 (optional, backward-compatible): pass `channels` to override the
// options derived from `conv`, and `convFor(channel) => Promise<conv>` to
// resolve (or create) the conversation to send on at send time — the Inbox
// thread composer uses both since it isn't tied to one conversation.
export default function MessageComposer({ conv, transports, status, onSent, onBlocked, channels, convFor }) {
  const options = channels || sendableChannels(conv, transports);
  const [channel, setChannel] = useState(options.includes(conv.channel) ? conv.channel : options[0]);
  const [draft, setDraft] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const blockedId = useId();
  const canManage = canManageTemplates(useAuth()?.user);
  // Ticket 195: formatted email. `html` is the rich editor's content, `draft`
  // its plain text (and the whole message for SMS); `editorKey` remounts the
  // editor to reset it. The signature is the user's own, empty until written.
  const [html, setHtml] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [signature, setSignature] = useState('');
  const emailViaGhl = transports?.email === 'ghl';
  useEffect(() => {
    if (!emailViaGhl) return undefined;
    let alive = true;
    commsApi.getSignature().then((r) => { if (alive) setSignature(r?.signature || ''); }).catch(() => {});
    return () => { alive = false; };
  }, [emailViaGhl]);
  // The signature arrives (or changes) after the editor mounted: start the
  // editor over with it, unless something is already typed.
  useEffect(() => { if (!html) setEditorKey((k) => k + 1); }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (options.length === 0) {
    return (
      <div className="p-3 border-t border-slate-100">
        <p className="text-xs text-amber-700" data-testid="ghl-reply-notice">
          {conv.source === 'ghl'
            ? 'This conversation is managed in GoHighLevel. Reply from GoHighLevel for now.'
            : 'Messaging from this inbox is not connected.'}
        </p>
      </div>
    );
  }

  const active = options.includes(channel) ? channel : options[0];
  const viaGhl = transports?.[active] === 'ghl';
  const needsSubject = active === 'email' && viaGhl;
  const blocked = blockText(status, active);
  // A formatted editor only for email through GHL; the signature alone is not a message.
  const rich = needsSubject;
  const body = draft.trim();
  const hasContent = body && squash(body) !== squash(signature);
  const ready = !blocked && hasContent && (!needsSubject || subject.trim());
  const startHtml = html || signatureHtml(signature);

  // Ticket 195: a template fills an empty draft or goes on a new line after
  // the text already typed; its subject fills an empty email subject.
  const applyTemplate = (t) => {
    if (rich) {
      // The formatted editor is uncontrolled: set its content and remount it.
      // An empty (or signature-only) editor gets the template above the signature.
      const paras = String(t.body).split('\n').map((l) => `<p>${l.trim() ? escapeHtml(l) : '<br>'}</p>`).join('');
      setHtml(hasContent ? `${html}${paras}` : `${paras}${html || signatureHtml(signature)}`);
      setDraft(hasContent ? `${draft.replace(/\s+$/, '')}\n${t.body}` : `${t.body}\n\n${signature}`.trim());
      setEditorKey((k) => k + 1);
    } else {
      setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, '')}\n${t.body}` : t.body));
    }
    if (t.subject && !subject.trim()) setSubject(t.subject);
    setTemplateId(t.template_id);
    setError('');
  };

  const send = async () => {
    if (!ready || sending) return;
    setSending(true);
    setError('');
    try {
      // convFor resolves (creating if needed) the contact's conversation for
      // the chosen channel; without it, send on the conv already given.
      const targetConv = convFor ? await convFor(active) : conv;
      const payload = { channel: active, body: draft };
      if (needsSubject) payload.subject = subject.trim();
      if (templateId) payload.template_id = templateId;
      if (rich) payload.html = html;
      let m;
      try {
        m = await commsApi.sendMessage(targetConv.conversation_id, payload);
      } catch (e) {
        // Ticket 171: outside the send window the server refuses until the LO confirms.
        const qh = quietHoursDetail(e);
        if (!qh) throw e;
        if (!(await confirmQuietHours(qh))) return; // keep the draft, no error
        m = await commsApi.sendMessage(targetConv.conversation_id, { ...payload, confirm_quiet_hours: true });
      }
      setDraft('');
      setHtml('');
      setEditorKey((k) => k + 1);
      setSubject('');
      setTemplateId(null);
      onSent?.(m, targetConv);
      // Ticket 152: GHL may have delivered this; the row stays, resending is the risk.
      if (m?.status === 'unconfirmed') toast({ title: 'Send not confirmed', description: UNCONFIRMED_HINT });
    } catch (e) {
      // The spend-approval 403 already got its one toast from api.js.
      if (isSpendApprovalError(e)) return;
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' && d ? d : (d?.message || 'Send failed'));
      if ([409, 503].includes(e?.response?.status)) onBlocked?.();
    } finally { setSending(false); }
  };

  return (
    <div className="p-3 border-t border-slate-100" data-testid="message-composer">
      <div className="mb-1.5 flex items-center">
        <TemplatePicker channel={active} onPick={applyTemplate} canManage={canManage} />
      </div>
      {needsSubject && (
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject" className="mb-2 h-9" data-testid="message-subject" />
      )}
      <div className="flex items-stretch gap-2">
        <Select value={active} onValueChange={(v) => { setChannel(v); setError(''); setTemplateId(null); }}>
          <SelectTrigger className="w-24 h-auto shrink-0" data-testid="message-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((ch) => <SelectItem key={ch} value={ch}>{LABELS[ch]}</SelectItem>)}
          </SelectContent>
        </Select>
        {rich ? (
          <RichEmailEditor key={editorKey} initialHtml={startHtml}
            onChange={(h, t) => { setHtml(h); setDraft(t); if (!t.trim()) setTemplateId(null); }} />
        ) : (
          <Textarea value={draft} onChange={(e) => { setDraft(e.target.value); if (!e.target.value.trim()) setTemplateId(null); }}
            placeholder="Type a message..." rows={2}
            className="min-h-[64px] resize-none" data-testid="message-draft" />
        )}
        <button onClick={send} disabled={sending || !ready} data-testid="send-message-btn"
          aria-describedby={blocked ? blockedId : undefined}
          className="btn-gold shrink-0 justify-center !rounded-md !px-4 !py-0">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
          Send
        </button>
      </div>
      {rich && (
        <div className="mt-1.5">
          <SignatureEditor signature={signature} onSaved={(sig) => {
            // Nothing but the old signature in the box: start over with the new one.
            if (!hasContent) { setHtml(''); setDraft(''); }
            setSignature(sig);
          }} />
        </div>
      )}
      {blocked && (
        <p id={blockedId} className="text-xs text-amber-700 mt-2" data-testid="composer-blocked">
          Can't send {LABELS[active]}: {blocked}.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 mt-2" role="alert" data-testid="composer-error">{error}</p>
      )}
      {transports?.sms === 'off' && (
        <p className="text-[10px] text-amber-700 mt-2" data-testid="sms-off-notice">
          Texting runs in GoHighLevel for now. SMS from this inbox is not connected.
        </p>
      )}
      {/* Ticket 195: no outbound attachments; documents come in through the secure upload link. */}
      <p className="text-[10px] text-slate-400 mt-2" data-testid="no-attachments-note">
        Files can't be attached here. To collect documents, send the secure upload link (Income Calculator, Send to Borrower).
      </p>
      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
        <Shield size={10} /> Suppression, revoked consent and GoHighLevel DND are blocked automatically.
        {viaGhl && <span data-testid="ghl-send-footer"> · Sends via GoHighLevel</span>}
      </p>
    </div>
  );
}
